/**
 * csvStats.js -- Turn the admin's csv-parse-metadata stats into form values.
 *
 * For an EV availability file, the admin returns the first, smallest and
 * largest state of charge above zero as fractions (0-1). The form shows them
 * as percentages in SOC fields that are normally disabled, because they come
 * from the file.
 *
 * A file whose SOC column is all zeros states no departure or return targets.
 * The sim accepts it, but no SOC can be inferred, so the admin returns null for
 * all three with a warning. Then the fields are cleared and made editable, so
 * the user enters the SOC limits themselves instead of being stuck with blank,
 * disabled, required fields.
 */

import { fracToPct } from "./units";

const toPct = (v) => (v == null ? "" : fracToPct(v));

/**
 * Form values for an EV's SOC fields from csv-parse-metadata `data_stats`.
 *
 * @param {object} dataStats - `data_stats` from the admin response.
 * @returns {{initial_soc: (number|string), min_soc: (number|string),
 *            max_soc: (number|string), socFromCsv: boolean}}
 *   Percentages ("" where the file gave none), and whether all three came
 *   from the file. When socFromCsv is false, the SOC fields should be editable.
 */
export function evSocFromCsvStats(dataStats) {
  const values = {
    initial_soc: toPct(dataStats?.initial_value),
    min_soc: toPct(dataStats?.min_value),
    max_soc: toPct(dataStats?.max_value),
  };
  const socFromCsv =
    dataStats?.initial_value != null &&
    dataStats?.min_value != null &&
    dataStats?.max_value != null;
  return { ...values, socFromCsv };
}

/**
 * The warnings list from a csv-parse-metadata response, or [] when there is
 * none (older admin versions don't send one).
 */
export function csvWarnings(response) {
  return Array.isArray(response?.warnings) ? response.warnings : [];
}
